import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, Globe, Calendar, Clock, Ticket, ExternalLink,
  Music, ChevronRight, Star, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';

const API = "/api";

const VenuePage = () => {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const res = await axios.get(`${API}/venues/${slug}`);
        setVenue(res.data);
      } catch {
        setVenue(null);
      }
      setLoading(false);
    };
    fetchVenue();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto pb-24 space-y-6" data-testid="venue-loading">
        <Skeleton className="h-56 w-full rounded-xl" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold mb-2">Venue not found</h2>
        <Link to="/calendar"><Button variant="outline">Back to Events</Button></Link>
      </div>
    );
  }

  const groupedEvents = {};
  (venue.events || []).forEach(e => {
    const d = e.date || 'Unknown';
    if (!groupedEvents[d]) groupedEvents[d] = [];
    groupedEvents[d].push(e);
  });
  const sortedDates = Object.keys(groupedEvents).sort();

  return (
    <div className="max-w-5xl mx-auto pb-24" data-testid="venue-page">
      {/* Back link */}
      <Link to="/calendar" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-purple-400 transition mb-4">
        <ArrowLeft size={14} /> Back to Calendar
      </Link>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative rounded-xl overflow-hidden mb-6 border border-border"
      >
        <div className="h-48 sm:h-64 relative">
          <img
            src={venue.cover_image}
            alt={venue.name}
            className="w-full h-full object-cover"
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

          {/* Venue Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p className="text-xs text-purple-300 uppercase tracking-wider mb-1">{venue.neighborhood}</p>
                <h1 className="text-3xl sm:text-4xl font-black text-white">{venue.name}</h1>
                <div className="flex items-center gap-3 mt-2 text-sm text-gray-300">
                  <span className="flex items-center gap-1"><MapPin size={13} /> {venue.address}</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={venue.website} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20" data-testid="venue-website-btn">
                    <Globe size={14} className="mr-1" /> Visit Website
                  </Button>
                </a>
                <a href={venue.events_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-500" data-testid="venue-events-btn">
                    <Ticket size={14} className="mr-1" /> Full Schedule
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Info Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <InfoCard label="Vibe" value={venue.vibe} />
        <InfoCard label="Music" value={venue.genre_focus} />
        <InfoCard label="Upcoming" value={`${venue.total_upcoming} events`} />
        <a href={venue.map_url} target="_blank" rel="noopener noreferrer" className="block">
          <div className="p-3 rounded-xl border border-border bg-card hover:border-purple-500/40 transition h-full">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Directions</p>
            <p className="text-sm font-semibold text-purple-400 flex items-center gap-1 mt-0.5">Open Maps <ExternalLink size={11} /></p>
          </div>
        </a>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Events (main content) */}
        <div className="lg:col-span-2 space-y-5">
          {/* About */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">About {venue.short_name}</h2>
            <p className="text-sm text-gray-300 leading-relaxed">{venue.description}</p>
          </motion.div>

          {/* Upcoming Events by date */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Calendar size={18} className="text-purple-400" /> Upcoming Events
              </h2>
              <a href={venue.events_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
                Full schedule on their site <ExternalLink size={10} />
              </a>
            </div>

            {sortedDates.length === 0 ? (
              <div className="text-center py-10 rounded-xl border border-border bg-card/30">
                <Calendar size={36} className="mx-auto mb-2 text-gray-600" />
                <p className="text-gray-500 text-sm">No upcoming events listed yet</p>
                <a href={venue.events_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:underline mt-1 inline-block">Check their website for the latest</a>
              </div>
            ) : (
              <div className="space-y-5">
                {sortedDates.map(dateStr => {
                  const dayEvents = groupedEvents[dateStr];
                  const dateObj = new Date(dateStr + 'T12:00:00');
                  const isToday = new Date().toLocaleDateString() === dateObj.toLocaleDateString();
                  const dayLabel = isToday ? 'Today' : dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                  const dateLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                  return (
                    <div key={dateStr}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center shrink-0 ${isToday ? 'bg-orange-500 text-white' : 'bg-card border border-border'}`}>
                          <span className="text-[9px] font-semibold uppercase">{dateObj.toLocaleDateString('en-US', { month: 'short' })}</span>
                          <span className="text-base font-bold leading-none">{dateObj.getDate()}</span>
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${isToday ? 'text-orange-400' : ''}`}>{dayLabel}</p>
                          <p className="text-[10px] text-gray-500">{dateLabel}</p>
                        </div>
                      </div>
                      <div className="space-y-2 ml-[52px]">
                        {dayEvents.map(event => (
                          <EventRow key={event.id} event={event} venueUrl={venue.events_url} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Quick Links */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Links</h3>
            <div className="space-y-2">
              <QuickLink href={venue.website} label="Official Website" icon={<Globe size={14} />} />
              <QuickLink href={venue.events_url} label="Full Event Calendar" icon={<Calendar size={14} />} />
              <QuickLink href={venue.map_url} label="Get Directions" icon={<MapPin size={14} />} />
            </div>
          </motion.div>

          {/* Featured Artists at this venue */}
          {venue.artists && venue.artists.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Star size={13} className="text-yellow-400" /> Artists at {venue.short_name}
              </h3>
              <div className="space-y-2">
                {venue.artists.map(artist => (
                  <Link key={artist.id} to={`/artist/${artist.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition">
                    <img src={artist.image_url} alt={artist.name} className="w-10 h-10 rounded-lg object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100'; }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{artist.name}</p>
                      <p className="text-[10px] text-gray-500">{artist.genre}</p>
                    </div>
                    <ChevronRight size={14} className="text-gray-600" />
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Photo Gallery */}
          {venue.gallery && venue.gallery.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Gallery</h3>
              <div className="grid grid-cols-3 gap-1.5">
                {venue.gallery.slice(0, 6).map((img, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden">
                    <img src={img} alt={`${venue.short_name} event`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* CTA to venue site */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <a href={venue.website} target="_blank" rel="noopener noreferrer" className="block p-4 rounded-xl bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/20 hover:border-purple-500/40 transition text-center" data-testid="venue-cta">
              <p className="text-sm font-semibold text-purple-300">Want more details?</p>
              <p className="text-xs text-gray-400 mt-0.5">Visit {venue.short_name}'s website for menus, reservations, and the full experience</p>
              <span className="inline-flex items-center gap-1 text-xs text-purple-400 mt-2">
                {venue.website.replace('https://', '').replace('http://', '').replace(/\/$/, '')} <ExternalLink size={10} />
              </span>
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="p-3 rounded-xl border border-border bg-card">
    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-xs font-semibold mt-0.5 text-gray-300 line-clamp-2">{value}</p>
  </div>
);

const EventRow = ({ event, venueUrl }) => {
  const isToday = new Date().toLocaleDateString() === new Date(event.date + 'T12:00:00').toLocaleDateString();
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:border-purple-500/30 transition group" data-testid={`venue-event-${event.id}`}>
      {event.image_url && (
        <img src={event.image_url} alt={event.title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm truncate group-hover:text-purple-400 transition">{event.title}</h4>
        <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
          <span className="flex items-center gap-1"><Clock size={11} /> {event.time}</span>
          {event.genre && <span className="text-purple-400">{event.genre}</span>}
        </div>
        {event.description && <p className="text-[11px] text-gray-500 truncate mt-0.5">{event.description}</p>}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {isToday && <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] font-bold rounded text-center">TONIGHT</span>}
        {event.ticket_url ? (
          <a href={event.ticket_url.startsWith('http') ? event.ticket_url : `https://${event.ticket_url}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="px-2 py-1 bg-green-500/20 text-green-400 text-[10px] font-semibold rounded flex items-center gap-0.5 hover:bg-green-500/30 transition">
            <Ticket size={10} /> Tickets
          </a>
        ) : (
          <a href={venueUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-purple-500/10 text-purple-400 text-[10px] rounded flex items-center gap-0.5 hover:bg-purple-500/20 transition">
            More Info <ExternalLink size={9} />
          </a>
        )}
      </div>
    </div>
  );
};

const QuickLink = ({ href, label, icon }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-lg text-sm text-gray-400 hover:text-purple-400 hover:bg-purple-500/5 transition">
    {icon}
    <span className="flex-1">{label}</span>
    <ExternalLink size={11} />
  </a>
);

export default VenuePage;
